import { Link } from "react-router-dom";
import { ArrowRight, Eye } from "lucide-react";

export const SpecsTable = ({ specs, testid }) => (
  <dl className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm" data-testid={testid}>
    {specs.map(([k, v]) => (
      <div key={k} className="grid grid-cols-5 gap-3 px-3 py-2">
        <dt className="col-span-2 text-slate-500">{k}</dt>
        <dd className="col-span-3 font-medium text-slate-900">{v}</dd>
      </div>
    ))}
  </dl>
);

export const UseChips = ({ uses }) => (
  <div className="flex flex-wrap gap-1.5">
    {uses.map((u) => <span key={u} className="rounded-full bg-[#F4F6F2] px-2.5 py-0.5 text-[11px] font-medium text-slate-700">{u}</span>)}
  </div>
);

export const ModelsTable = ({ models }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="product-models-table">
    <table className="w-full min-w-[640px] text-left text-sm">
      <thead className="bg-[#F4F6F2] text-[11px] uppercase tracking-wider text-slate-500">
        <tr>{["Model", "Description", "Open size (cm)", "Folded / packing (cm)", "Load", "Weight"].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {models.map((m) => (
          <tr key={m.code} data-testid={`model-row-${m.code}`}>
            <td className="mono whitespace-nowrap px-3 py-3 font-semibold text-[#C20500]">{m.code}</td>
            <td className="px-3 py-3"><p className="font-medium text-slate-900">{m.name}</p><p className="text-xs text-slate-500">{m.features}</p></td>
            <td className="whitespace-nowrap px-3 py-3 text-slate-700">{m.open}</td>
            <td className="whitespace-nowrap px-3 py-3 text-slate-700">{m.folded}</td>
            <td className="whitespace-nowrap px-3 py-3 text-slate-700">{m.load}</td>
            <td className="whitespace-nowrap px-3 py-3 text-slate-700">{m.weight}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const QuoteButton = ({ p, className = "" }) => (
  <Link to={`/enquiry?product=${p.id}`} data-testid={`quote-button-${p.id}`} className={`btn-primary px-4 py-2.5 ${className}`}>
    Get a quote <ArrowRight className="h-4 w-4" />
  </Link>
);

export const ProductCard = ({ p }) => (
  <article id={p.id} className="card-base group flex flex-col overflow-hidden" data-testid={`product-card-${p.id}`}>
    <Link to={`/products/${p.id}`} className="relative aspect-square overflow-hidden bg-white" data-testid={`product-image-${p.id}`}>
      <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-contain p-6 mix-blend-multiply transition-transform duration-500 group-hover:scale-105" />
      {p.models?.length > 1 && <span className="absolute left-3 top-3 rounded-full bg-[#123634] px-2.5 py-1 text-[11px] font-semibold text-white" data-testid={`product-models-badge-${p.id}`}>{p.models.length} models</span>}
    </Link>
    <div className="flex flex-1 flex-col border-t border-slate-100 p-6">
      <h3 className="text-lg text-slate-900"><Link to={`/products/${p.id}`} className="hover:text-[#C20500]">{p.name}</Link></h3>
      <p className="mt-1 text-sm text-slate-600">{p.tagline}</p>
      <div className="mt-4"><SpecsTable specs={p.specs.slice(0, 3)} /></div>
      <div className="mt-auto flex gap-2 pt-5">
        <Link to={`/products/${p.id}`} className="btn-outline flex-1 px-3" data-testid={`product-details-${p.id}`}><Eye className="h-4 w-4" /> Details</Link>
        <QuoteButton p={p} className="flex-1" />
      </div>
    </div>
  </article>
);

export const ProductRow = ({ p, index }) => (
  <article id={p.id} className="card-base grid gap-6 overflow-hidden p-5 md:grid-cols-12 md:items-center md:p-6" data-testid={`product-card-${p.id}`}>
    <Link to={`/products/${p.id}`} className="relative aspect-square overflow-hidden rounded-lg bg-white md:col-span-3" data-testid={`product-image-${p.id}`}>
      <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-contain p-4 mix-blend-multiply transition-transform duration-500 hover:scale-105" />
    </Link>
    <div className="md:col-span-4">
      <p className="mono text-[11px] text-slate-400">{String(index + 1).padStart(2, "0")}</p>
      <h3 className="mt-1 text-xl text-slate-900"><Link to={`/products/${p.id}`} className="hover:text-[#C20500]">{p.name}</Link></h3>
      <p className="mt-2 text-sm text-slate-600">{p.tagline}</p>
      <p className="mono mt-4 text-[10px] uppercase tracking-[0.18em] text-slate-400">Where it's used</p>
      <div className="mt-2"><UseChips uses={p.uses} /></div>
      <div className="mt-5 flex flex-wrap gap-2">
        <Link to={`/products/${p.id}`} className="btn-outline px-4 py-2.5" data-testid={`product-details-${p.id}`}><Eye className="h-4 w-4" /> Details</Link>
        <QuoteButton p={p} />
      </div>
    </div>
    <div className="md:col-span-5"><SpecsTable specs={p.specs} testid={`product-specs-${p.id}`} /></div>
  </article>
);
